import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Drawer, List, ListItem, ListItemText, Box, Toolbar, IconButton, Tooltip, ListItemIcon, Popover, Collapse, Snackbar, Alert } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import api from '../api';
import Swal from "sweetalert2";
import { useToastActions } from '../customcomponents/CustomToast';

function Sidebar({ open, expanded, onToggle, menuItems, drawerWidth, collapsedWidth, location }) {
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [submenuItems, setSubmenuItems] = useState([]);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState(null);
  const [reminderData, setReminderData] = useState(null);
  const [sidebarPermissions, setSidebarPermissions] = useState([]);
  const { socket: socketContext, isConnected: isSocketConnected, on } = useSocket();

  const navigate = useNavigate();
  const toast = useToastActions();
  const reminderCheckInterval = useRef(null);
  const processedReminders = useRef(new Set());
  const pathName = useLocation().pathname;
  const userRole = localStorage.getItem('userRole') || null;

  useEffect(() => {
    if (!isSocketConnected && !socketContext) return;
    
    const handleTicketUpdate = (data) => {
      console.log({ data });
      if (data?.callType == 1) {
        toast.success('Received a call from ' + (data?.caller_name || data?.caller_number.replace('+91', '')));
      }
      if (data?.callType == 3) {
        toast.warning('Received a missed call from ' + (data?.caller_name || data?.caller_number.replace('+91', '')));
      }
    };

    socketContext.on('ticket_updated_calling', handleTicketUpdate);
    
    return () => {
      socketContext.off('ticket_updated_calling', handleTicketUpdate);
    };
  }, [isSocketConnected, socketContext]);

  const handleSubmenuClick = (event, items, idx) => {
    setSubmenuAnchor(event.currentTarget);
    setSubmenuItems(items);
    setOpenSubmenuIndex(idx);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setSubmenuItems([]);
    setOpenSubmenuIndex(null);
  };

  // Fetch reminders on component mount
  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    
    const fetchReminders = async () => {
      try {
        const response = await api.get('/reminder/getReminderData', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setReminderData(response?.data?.reminderData);
      } catch (error) {
        console.error('Error fetching reminders:', error);
      }
    };
    
    fetchReminders();
    
    // Set up interval to refresh reminders periodically
    const refreshInterval = setInterval(fetchReminders, 12000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  // Fetch sidebar permissions - initial load and periodic refresh
  useEffect(() => {
    const fetchSidebarPermissions = async () => {
      try {
        const response = await api.get('/page-settings/sidebar', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setSidebarPermissions(response?.data?.data || []);
        // console.log('Sidebar permissions refreshed:', response?.data?.data);
      } catch (error) {
        console.error('Error fetching sidebar permissions:', error);
        setSidebarPermissions([]);
      }
    };

    // Fetch on mount
    fetchSidebarPermissions();

    // Refresh every 5 seconds to keep permissions in sync
    const refreshInterval = setInterval(fetchSidebarPermissions, 5000);

    return () => clearInterval(refreshInterval);
  }, []);

  const optimizeReminderData = useMemo(() => {
    if (!reminderData) {
      return [];
    }
    return reminderData.filter((item) => {
      return item?.TRM_IsActivated !== true;
    });
  }, [reminderData]);

  // Activate reminder function
  const activateReminder = async (id, ticketId) => {
    try {
      // Skip if already processing this reminder
      if (processedReminders.current.has(id)) {
        return;
      }
      
      processedReminders.current.add(id);
      
      const res = await api.post('/reminder/activateReminder', {
        id: id,
        ticketId: ticketId,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (res?.data?.success) {
        await socketContext.emit('live-chat-ticket', {
          customerId: 1,
          page: 1,
          limit: 10
        })
        Swal.fire({
          title: "Schedule Reminder Activated!",
          text: new Date(res?.data?.data?.TRM_DateTime_ISO).toLocaleString() || "Your reminder was successfully added.",
          icon: "success",
          confirmButtonText: "Go to Interactions",
          confirmButtonColor: "#2563eb", // blue
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then((result) => {
          if (result?.isConfirmed) {
            navigate('/interactions-new');
            if(pathName === '/interactions-new') window.location.reload();
          }
        });
        
        // Update local state
        setReminderData(prev => prev?.filter((item) => item?._id !== id) || []);
      }
    } catch (error) {
      console.error('Error activating reminder:', error);
      // Remove from processed set on error so it can be retried
      processedReminders.current.delete(id);
    }
  };

  // Function to get current time in "HH:mm" format
  const getCurrentTimeFormatted = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Check for due reminders when time matches exactly
  useEffect(() => {
    if (optimizeReminderData.length === 0) return;

    const checkDueReminders = () => {
      const currentTime = getCurrentTimeFormatted();
      
      optimizeReminderData.forEach((reminder) => {
        if (!reminder?._id || !reminder?.TRM_Reminder_Time) return;
        
        // Trigger reminder only when times match exactly
        if (reminder.TRM_Reminder_Time === currentTime && !processedReminders.current.has(reminder._id)) {
          console.log('Activating due reminder at exact time:', reminder._id, currentTime);
          activateReminder(reminder._id, reminder.TRM_Ticket_Ref);
        }
      });
    };

    // Check immediately
    checkDueReminders();

    // Set up interval to check every minute
    reminderCheckInterval.current = setInterval(checkDueReminders, 60000);

    return () => {
      if (reminderCheckInterval.current) {
        clearInterval(reminderCheckInterval.current);
        reminderCheckInterval.current = null;
      }
    };
  }, [optimizeReminderData]);

  // Hide Map for admin - LEGACY (now uses permission-based system)
  const restrictedPaths = [
    // Paths can now be managed via sidebar permissions UI instead of hardcoding
  ];

  // Check if a menu item should be displayed based on sidebar permissions
  const isMenuItemAllowed = (itemPath) => {
    // If no permissions are loaded, show all items by default
    if (sidebarPermissions.length === 0) {
      return true;
    }

    // Find the permission entry for this path
    const permission = sidebarPermissions.find(p => p.path === itemPath);

    // If no permission entry exists, show by default (safe default for backwards compatibility)
    if (!permission) {
      return true;
    }

    // If the permission is inactive, hide it
    if (!permission.active) {
      return false;
    }

    // If there are roles assigned, check if user's role is in the list
    // If there are NO roles assigned, hide the item (permission explicitly removed)
    if (permission.roles && permission.roles.length > 0) {
      const userRoleId = localStorage.getItem('userRoleId');
      const isRoleAssigned = permission.roles.some(r => r.id === userRoleId);
      return isRoleAssigned;
    }

    // If roles array is empty, hide the item (no roles have access)
    return false;
  };

  const filteredMenu = menuItems.filter(item => {
    // First check legacy restriction (for backwards compatibility)
    if (userRole?.toLowerCase() !== "superadmin" && restrictedPaths.includes(item.path)) {
      return false;
    }
    // Then check new permission-based system
    return isMenuItemAllowed(item.path);
  });



  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: expanded ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: expanded ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          transition: 'width 0.2s',
          overflowX: 'hidden',
        },
      }}
    >
      <Box>
        <Toolbar sx={{ display: 'flex', justifyContent: expanded ? 'flex-end' : 'center', alignItems: 'center' }}>
          <IconButton onClick={onToggle} edge="start" color="inherit" aria-label="menu">
            <MenuIcon />
          </IconButton>
        </Toolbar>
        <List>
          {filteredMenu.map((item, idx) => {
            const isSelected = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            return (
              <React.Fragment key={item.text}>
                <Tooltip title={expanded ? '' : item.text} placement="right">
                  <ListItem
                    button
                    component={item.path ? Link : 'div'}
                    to={item.path || undefined}
                    selected={isSelected}
                    sx={{
                      justifyContent: expanded ? 'initial' : 'center',
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#1976d28a',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#1976d2 ',
                          transition: 'background-color 0.4s',
                        },
                      },
                    }}
                    onClick={
                      hasChildren
                        ? (e) => {
                            if (!expanded) {
                              handleSubmenuClick(e, item.children, idx);
                            }
                          }
                        : undefined
                    }
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: expanded ? 2 : 'auto',
                        justifyContent: 'center',
                        color: isSelected ? '#ffffff' : 'background.text',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {expanded && <ListItemText primary={item.text} sx={{ color: isSelected ? '#ffffff' : 'background.text' }} />}
                    {hasChildren && expanded && (openSubmenuIndex === idx ? <ExpandLess /> : <ExpandMore />)}
                  </ListItem>
                </Tooltip>
                {/* Expanded: show nested items inline */}
                {hasChildren && expanded && (
                  <Collapse in={openSubmenuIndex === idx} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => (
                        <ListItem
                          button
                          component={Link}
                          to={child.path}
                          key={child.text}
                          selected={location.pathname === child.path}
                          sx={{
                            pl: 6,
                            backgroundColor: location.pathname === child.path ? '#00796b' : 'transparent',
                            '&.Mui-selected': {
                              backgroundColor: '#00796b',
                              color: '#ffffff',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 2,
                              justifyContent: 'center',
                              color: location.pathname === child.path ? '#ffffff' : 'inherit',
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.text} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
        {/* Collapsed: floating submenu popover */}
        <Popover
          open={Boolean(submenuAnchor)}
          anchorEl={submenuAnchor}
          onClose={handleSubmenuClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          PaperProps={{ sx: { minWidth: 180, p: 1 } }}
        >
          <List>
            {submenuItems.map((child) => (
              <ListItem
                button
                component={Link}
                to={child.path}
                key={child.text}
                onClick={handleSubmenuClose}
                sx={{ pl: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>{child.icon}</ListItemIcon>
                <ListItemText primary={child.text} />
              </ListItem>
            ))}
          </List>
        </Popover>
      </Box>
    </Drawer>
  );
}

export default Sidebar;